import React, { useCallback, useEffect, useState } from "react";
import CssBaseline from "@mui/material/CssBaseline";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  deleteIngrediant,
  getIngrediantsPaginated,
  getIngrediantsError,
  getIngrediantsStatus,
  selectAllIngrediants,
  selectIngrediantsPagination,
  selectIngrediantsLastParams,
  selectIngrediantsQuery,
  setIngrediantsQuery,
  resetIngrediantsQuery,
  getIngrediantsSuccess,
  updateStatus,
} from "../../../features/ingrediantSlice";
import Loading from "../../../components/loading";
import Error from "../../../components/Error";
import ProductCard from "../../../components/card";
import NoData from "../../../components/noData";
import AppBarSearch from "../../../global/appBarSearch";
import { Box, Grid, TablePagination } from "@mui/material";
import AlertDialog from "../../../components/alertDialog";
import { toast } from "react-toastify";
import { useRestaurant } from "../../../hooks/RestaurantContext";
import FiltersDialog, {
  FiltersButton,
} from "../../../components/filters/filtersDialog";
import FilterChipGroup from "../../../components/filters/filterChipGroup";
import { StockList, VisibilityList } from "../../../models/product.model";
import { NamePriceSortList, applySort } from "../../../models/sort.model";
import { ResponsiveProvider } from "../../../hooks/uiHook";
import ShowMultiItems from "../../../components/showMultiItems";
import { CardField, CardStatus } from "../../../components/cardMeta";
import ListViewIngrediants from "./listViewIngrediants";
import { useIsCompactHeader } from "../../../hooks/uiHook";
import { useTranslation } from "react-i18next";

const Ingrediant = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { restaurantId } = useRestaurant();
  const isCompactHeader = useIsCompactHeader();
  const [visibilityFilter, setVisibilityFilter] = useState("");
  const [stockFilter, setStockFilter] = useState("");
  const [sortFilter, setSortFilter] = useState("");
  const [filtersDialogOpen, setFiltersDialogOpen] = useState(false);
  // Draft copies bound to the FiltersDialog controls; committed to the
  // applied state above only when Appliquer is clicked.
  const [draftVisibilityFilter, setDraftVisibilityFilter] = useState("");
  const [draftStockFilter, setDraftStockFilter] = useState("");
  const [draftSortFilter, setDraftSortFilter] = useState("");
  const [viewMode, setViewMode] = useState("grid");
  const query = useSelector(selectIngrediantsQuery);
  const { search, limit } = query;
  const page = query.page - 1;
  const [open, setOpen] = useState(false);
  const [cardId, setCardId] = useState(null);
  const status = useSelector(getIngrediantsStatus);
  const error = useSelector(getIngrediantsError);
  const ingrediants = useSelector(selectAllIngrediants);
  const pagination = useSelector(selectIngrediantsPagination);
  const lastFetchedParams = useSelector(selectIngrediantsLastParams);
  const success = useSelector(getIngrediantsSuccess);
  const totalCount = pagination.totalRecords || 0;

  const handleClickOpen = (cardId) => {
    setOpen(true);
    setCardId(cardId);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    navigate("add");
  };

  const handleModify = (data) => {
    navigate("modify", { state: { ingrediant: data } });
  };

  // Reset the search/pagination query on every fresh mount (not just on
  // restaurant switch, handled separately below) so navigating away (e.g. to
  // Add/Modify, which unmounts this whole screen) and back always starts
  // from a clean search, consistent with Utilisateurs/Commandes where search
  // is local state that resets in full. Registered above the fetch effect.
  useEffect(() => {
    dispatch(resetIngrediantsQuery());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let effectiveSearch = search;
    let effectiveLimit = limit;
    let effectivePage = page + 1;

    if (lastFetchedParams) {
      let previousRestaurantId;
      try {
        previousRestaurantId = JSON.parse(lastFetchedParams).restaurantId;
      } catch {
        previousRestaurantId = undefined;
      }
      if (previousRestaurantId !== restaurantId) {
        effectiveSearch = "";
        effectiveLimit = 12;
        effectivePage = 1;
        dispatch(resetIngrediantsQuery());
      }
    }

    const params = {
      restaurantId,
      search: effectiveSearch,
      limit: effectiveLimit,
      page: effectivePage,
    };
    const serialized = JSON.stringify(params);
    if (lastFetchedParams === serialized && status === "fetchDataPaginated") {
      return;
    }
    dispatch(getIngrediantsPaginated(params));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, restaurantId, search, limit, page]);

  // Live draft-vs-applied diff: Appliquer stays disabled until at least one
  // draft filter differs from what's currently applied, and re-disables
  // itself automatically if the user changes a filter back to match.
  const hasPendingChanges =
    draftVisibilityFilter !== visibilityFilter ||
    draftStockFilter !== stockFilter ||
    draftSortFilter !== sortFilter;

  // Visibility/stock are not (yet) supported as query params by the backend
  // /ingrediant endpoint, so they're applied client-side on the current page,
  // then sorted.
  const filteredIngrediantsMemo = applySort(
    (ingrediants || []).filter((ingrediant) => {
      if (
        visibilityFilter !== "" &&
        String(ingrediant.visible) !== String(visibilityFilter)
      ) {
        return false;
      }
      if (
        stockFilter !== "" &&
        String(ingrediant.outOfStock) !== String(stockFilter)
      ) {
        return false;
      }
      return true;
    }),
    sortFilter
  );

  useEffect(() => {
    if (status === "deleteSuccess") {
      toast.success(success);
      dispatch(updateStatus());
    } else if (status === "deleteError") {
      toast.error(error);
    }
  }, [cardId, dispatch, error, status, success]);

  const handleChangePage = useCallback(
    (event, newPage) => {
      const maxPage = Math.max(0, Math.ceil(totalCount / limit) - 1);
      const clampedPage = Math.min(newPage, maxPage);
      dispatch(setIngrediantsQuery({ page: clampedPage + 1 }));
    },
    [dispatch, limit, totalCount]
  );

  const handleChangeRowsPerPage = useCallback(
    (event) => {
      const newLimit = Number(event.target.value);
      dispatch(setIngrediantsQuery({ limit: newLimit, page: 1 }));
    },
    [dispatch]
  );

  const effectiveViewMode = isCompactHeader ? "grid" : viewMode;

  return (
    <div className="main-application">
      <CssBaseline />
      <ResponsiveProvider maxWidth={900}>
        <AppBarSearch
          disableMobileDrawer
          searchText={t("search.byName")}
          handleSubmit={handleSubmit}
          handleSearch={(e) => {
            dispatch(setIngrediantsQuery({ search: e.target.value, page: 1 }));
          }}
          title={t("pages.myIngredients")}
          buttonTitle={t("actions.addIngredient")}
          viewMode={effectiveViewMode}
          onToggleViewMode={
            isCompactHeader
              ? undefined
              : () => setViewMode(viewMode === "list" ? "grid" : "list")
          }
          children={
            <>
              <FiltersButton
                onClick={() => {
                  setDraftVisibilityFilter(visibilityFilter);
                  setDraftStockFilter(stockFilter);
                  setDraftSortFilter(sortFilter);
                  setFiltersDialogOpen(true);
                }}
                activeCount={
                  (visibilityFilter !== "" ? 1 : 0) +
                  (stockFilter !== "" ? 1 : 0) +
                  (sortFilter ? 1 : 0)
                }
              />
            </>
          }
        />
      </ResponsiveProvider>
      <FiltersDialog
        open={filtersDialogOpen}
        onClose={() => setFiltersDialogOpen(false)}
        onReset={() => {
          setDraftVisibilityFilter("");
          setVisibilityFilter("");
          setDraftStockFilter("");
          setStockFilter("");
          setDraftSortFilter("");
          setSortFilter("");
          setFiltersDialogOpen(false);
        }}
        onApply={() => {
          setVisibilityFilter(draftVisibilityFilter);
          setStockFilter(draftStockFilter);
          setSortFilter(draftSortFilter);
          setFiltersDialogOpen(false);
        }}
        disableApply={!hasPendingChanges}
      >
        <FilterChipGroup
          label={t("filters.allVisibilities")}
          options={VisibilityList}
          value={draftVisibilityFilter}
          onChange={setDraftVisibilityFilter}
          allowAll
        />
        <FilterChipGroup
          label={t("filters.allStocks")}
          options={StockList}
          value={draftStockFilter}
          onChange={setDraftStockFilter}
          allowAll
        />
        <FilterChipGroup
          label={t("filters.sortBy")}
          options={NamePriceSortList}
          value={draftSortFilter}
          onChange={setDraftSortFilter}
        />
      </FiltersDialog>
      <main>
        <Box sx={{ mt: 2, mb: 2 }}>
          {effectiveViewMode === "list" ? (
            <ListViewIngrediants
              ingrediants={filteredIngrediantsMemo}
              page={page}
              limit={limit}
              handleClickOpen={handleClickOpen}
              handleModify={handleModify}
              status={status}
              error={error}
              search={search}
            />
          ) : (
          <Grid container spacing={2}>
            {status === "loadingFetchDataPaginated" ? (
              <Loading />
            ) : status === "fetchErrorPaginated" ? (
              <Error>{error}</Error>
            ) : (
              <>
                {filteredIngrediantsMemo && filteredIngrediantsMemo.length > 0 ? (
                  filteredIngrediantsMemo.map((card) => (
                    <ProductCard
                      key={card._id}
                      data={card}
                      handleModify={() => handleModify(card)}
                      handleClickOpen={() => handleClickOpen(card._id)}
                      search={search}
                      content={
                        <>
                          <CardField label={t("fields.price")}>
                            {card.price ?? 0}
                          </CardField>
                          <CardField label={t("fields.extraPrice")}>
                            {card.suppPrice ?? 0}
                          </CardField>
                          {card.types && (
                            <ShowMultiItems
                              text={t("fields.optionsLabel")}
                              items={card.types}
                            />
                          )}
                          <CardStatus
                            outOfStock={card.outOfStock}
                            visible={card.visible}
                          />
                        </>
                      }
                    />
                  ))
                ) : (
                  <NoData />
                )}
              </>
            )}
          </Grid>
          )}
          <AlertDialog
            handleClose={() => setOpen(false)}
            open={open}
            message={"Voulez-vous supprimer cet ingrédient ?"}
            cardId={cardId}
            deleteData={() =>
              dispatch(
                deleteIngrediant({ ingrediantId: cardId, restaurantId })
              )
            }
          />
          <TablePagination
            rowsPerPageOptions={[12, 24, 48]}
            component="div"
            count={totalCount}
            rowsPerPage={limit}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Box>
      </main>
    </div>
  );
};

export default Ingrediant;
